import { UserService } from '@/modules/global-settings/security/services/user.service';
import { make } from 'vuex-pathify';
import Logger from '@/lib/logger';
import { BranchService } from '../../company/services/branch.service';
// Create a new service instance for user module
const userService = new UserService();
// User module state
export const state = {
  nonUsers: [],
  users: [],
  user: {}
};
// User module mutations
export const mutations = {
  ...make.mutations(state)
};
// User module actions
export const actions = {
  /**
   * Load user page
   * @param {Object} Object - Vuex context
   * @returns {Array} Array of users
   */
  loadUserPage: async ({ dispatch }) => {
    try {
      const modules = ['userModule/fetchUsers', 'roleModule/fetchRoles'];

      for (const module of modules) {
        await dispatch(module, null, { root: true });
      }
    } catch (error) {
      Logger.error('loading user page');
      throw error;
    }
  },

  /**
   * Fetch users for export
   * @param { object }
   * @return Array of users
   */
  fetchUsersForExport: async (
    { },
    {
      pagination,
      search,
      waiverLevel,
      dsrWaiverLevel,
      approvalLevels,
      insuranceWaiverLevel
    } = {}
  ) => {
    try {
      const params = {
        page: pagination?.page || 1,
        limit: pagination?.itemsPerPage || 100,
        sort: pagination?.sortBy ? pagination.sortBy[0] : 'id',
        order: pagination?.sortDesc
          ? pagination.sortDesc[0]
            ? 'desc'
            : 'asc'
          : 'asc',
        searchParam: search,
        waiverLevel,
        dsrWaiverLevel,
        approvalLevels,
        insuranceWaiverLevel
      };
      const { data } = await userService.findAll(params);
      return data;
    } catch (error) {
      Logger.error('fetching users for export');
      throw error;
    }
  },

  /**
   * Fetch all users
   * @param {Object} Object - Vuex context
   * @returns {Array} Array of users
   */
  fetchUsers: async (
    { commit },
    {
      pagination,
      search,
      waiverLevel,
      dsrWaiverLevel,
      approvalLevels,
      insuranceWaiverLevel
    } = {}
  ) => {
    try {
      const params = {
        page: pagination?.page || 1,
        limit: pagination?.itemsPerPage || 100,
        sort: pagination?.sortBy ? pagination.sortBy[0] : 'id',
        order: pagination?.sortDesc
          ? pagination.sortDesc[0]
            ? 'desc'
            : 'asc'
          : 'asc',
        searchParam: search,
        waiverLevel,
        dsrWaiverLevel,
        approvalLevels,
        insuranceWaiverLevel
      };
      const { data } = await userService.findAll(params);
      commit('SET_USERS', data.users);
      return data;
    } catch (error) {
      Logger.error('fetching users');
      throw error;
    }
  },

  /**
   * Fetch a single user by id
   * @param {Object} Object - Vuex context
   * @param {string | number} id - User id
   * @returns {Object} User
   */
  fetchUser: async ({ commit }, id) => {
    try {
      const { data } = await userService.findOne(id);
      commit('SET_USER', data);
      return data;
    } catch (error) {
      Logger.error('fetching user');
      throw error;
    }
  },

  fetchNonUsers: async({ commit }) => {
    try {
      const { data } = await userService.fetchNonUsers({ skip: 0, limit: 100 });
      console.log(data);
      commit('SET_NON_USERS', data.persons);
      return data;
    } catch (error) {
      
    }
  },

  /**
   * Create a single user with the given data
   * @param {Object} Object - Vuex context
   * @param {Object} payload - User data
   * @returns {Object} Created user
   */
  createUser: async ({ dispatch }, payload) => {
    try {
      const { data } = await userService.create(payload);
      dispatch('fetchUsers');
      return data;
    } catch (error) {
      Logger.error('create user');
      throw error;
    }
  },

  async createUserPersonProfile({ dispatch }, { canOverWrite, userId, person }) {
    try {
      const { data } = await userService.createUserPersonProfile(userId, person, canOverWrite);
      console.log(data);
      
      dispatch('fetchUsers');
      return data;
    } catch (error) {
      Logger.error('create user person profile');
      throw error;
    }
  },

  /**
   * Update a single user with the given data
   * @param {Object} Object - Vuex context
   * @param {Object} payload - User data
   * @param {string | number} payload.id - User id
   * @param {Object} payload.data - User data
   * @returns {Object} Updated user
   */
  updateUser: async ({ dispatch }, { id, payload }) => {
    try {
      await userService.update(id, payload);
      dispatch('fetchUsers');
    } catch (error) {
      Logger.error('updating user');
      throw error;
    }
  },

  /**
   * Delete a single user by id
   * @param {Object} Object - Vuex context
   * @param {string | number} id - User id
   * @returns {void}
   */
  deleteUser: async ({ dispatch }, id) => {
    try {
      await userService.delete(id);
      dispatch('fetchUsers');
    } catch (error) {
      Logger.error('updating user');
      throw error;
    }
  },

  /**
   * Enable a single user account by id
   * @param {Object} Object - Vuex context
   * @param {string | number} id - User id
   * @returns {void}
   */
  enableUser: async ({ dispatch }, id) => {
    try {
      await userService.enable(id);
      dispatch('fetchUsers');
    } catch (error) {
      Logger.error('updating user');
      throw error;
    }
  },

  /**
   * Disable a single user account by id
   * @param {Object} Object - Vuex context
   * @param {string | number} id - User id
   * @returns {void}
   *
   * @private This will re-fetch all users
   */
  disableUser: async ({ dispatch }, id) => {
    try {
      await userService.disable(id);
      dispatch('fetchUsers');
    } catch (error) {
      Logger.error('updating user');
      throw error;
    }
  },

  /**
   * Generate a new user password for a user by id & send it to the user email
   * @param {Object} Object - Vuex context
   * @param {string | number} id - User id
   * @returns {void}
   *
   * @private This will re-fetch all users
   */
  resetPassword: async ({ dispatch }, id) => {
    try {
      await userService.resetPassword(id);
      dispatch('fetchUsers');
    } catch (error) {
      Logger.error('generate new user');
      throw error;
    }
  },

  /**
   * Update a single user password by id
   * @param {Object} Object - Vuex context
   * @param {string | number} id - User id
   * @returns {void}
   *
   * @private This will re-fetch all users
   */
  updatePassword: async ({ dispatch }, { id }) => {
    try {
      await userService.updatePassword({ id });
      dispatch('fetchUsers');
    } catch (error) {
      Logger.error('Updating password');
      throw error;
    }
  },

  /**
   * Update a single user password. The user must be logged in
   * @param {Object} Object - Vuex context
   * @param {string | number} id - User id
   * @returns {void}
   */
  newPassword: async (_, data) => {
    try {
      await userService.newPassword(data);
    } catch (error) {
      Logger.error('setting new password');
      throw error;
    }
  },

  /**
   * Upload a single user avatar by id
   * @param {Object} Object - Vuex context
   * @param {Object} data - User avatar data
   * @param {string | number} data.id - User id
   * @param {Object} data.data - User avatar data
   * @returns {void}
   */
  uploadAvatar: async (_, { data, id }) => {
    try {
      await userService.uploadAvatar(data, id);
    } catch (error) {
      Logger.error('generate new user');
      throw error;
    }
  },

  /**
   * Delete a single user avatar by id
   * @param {Object} Object - Vuex context
   * @param {Object} data - User avatar data
   * @param {string | number} data.id - User id
   * @returns {void}
   */
  deleteAvatar: async (_, { id }) => {
    try {
      await userService.deleteAvatar(id);
    } catch (error) {
      Logger.error('removing avatar');
      throw error;
    }
  },
  
  /**
   * Upload a single user signature by id
   * @param {Object} Object - Vuex context
   * @param {Object} data - User signature form
   * @param {string | number} data.id - User id
   * @returns {void}
   */
  uploadSignature: async (_, { data, id }) => {
    try {
      await userService.uploadSignature(data, id);
    } catch (error) {
      Logger.error('upload user signature');
      throw error;
    }
  },

  /**
   * Delete a single user signature by id
   * @param {Object} Object - Vuex context
   * @param {string | number} data.id - User id
   * @returns {void}
   */
  deleteSignature: async (_, { id }) => {
    try {
      await userService.deleteSignature(id);
    } catch (error) {
      Logger.error('removing user signature');
      throw error;
    }
  },
  
  
  async findAvatar(_, id) {
    try {
      const { data } = await userService.findAvatar(id);
      return data 
    } catch (error) {
      Logger.error('find new user avatar');
      throw error;
    }
  },

  /**
   * Generate a new user token for a user by id
   * @param {Object} Object - Vuex context
   * @param {string | number} id - User id
   * @returns {void}
   *
   * @private This will re-fetch all users
   */
  resetToken: async ({ dispatch }, id) => {
    try {
      await userService.resetToken(id);
      dispatch('fetchUsers');
    } catch (error) {
      Logger.error('generate new user');
      throw error;
    }
  },


  
  /**
   * reset a single user 2FA authentication
   * @param {string | number} id - User id
   * @returns {void}
   *
   * @private This will log out user
   */

  reset2FA: async ({ dispatch }, { id }) => {
    try {
      await userService.reset2FA(id);
      dispatch('fetchUsers');
    } catch (error) {
      Logger.error('Updating password');
      throw error;
    }
  },

  /**
   * Link an existing person profile to an existing user
   * @param {Object} Object - Vuex context
   * @param {Object} payload - { userId, personId }
   * @returns {Object} Updated user
   */
  unlinkPerson: async ({ dispatch }, userId) => {
    try {
      const { data } = await userService.unlinkPerson(userId);
      dispatch('fetchUsers');
      return data;
    } catch (error) {
      Logger.error('unlinking person from user');
      throw error;
    }
  },

  linkExistingPerson: async ({ dispatch }, { userId, personId }) => {
    try {
      const { data } = await userService.linkExistingPerson(userId, personId);
      dispatch('fetchUsers');
      return data;
    } catch (error) {
      Logger.error('linking existing person to user');
      throw error;
    }
  },

  /**
   * Search Staff persons who dont yet have a user account (for link/create flows)
   * @param {Object} _ - Vuex context (unused)
   * @param {string} query - Search string
   * @returns {Array} Matching persons
   */
  fetchAllStaffNonUsers: async (_) => {
    try {
      const { data } = await userService.fetchAllStaffNonUsers();
      return data?.persons || [];
    } catch (error) {
      Logger.error('fetching all staff non users');
      throw error;
    }
  },

  searchPersons: async (_, query) => {
    try {
      const { data } = await userService.searchPersons(query);
      return data?.persons || [];
    } catch (error) {
      Logger.error('searching persons');
      throw error;
    }
  },

  /**
   * Search all Staff persons by email
   * @param {Object} _ - Vuex context (unused)
   * @param {string} email - Email to search
   * @returns {Array} Matching persons
   */
  searchStaffPersonsByEmail: async (_, email) => {
    try {
      const { data } = await userService.searchStaffPersonsByEmail(email);
      return data?.persons || [];
    } catch (error) {
      Logger.error('searching staff persons by email');
      throw error;
    }
  },
};

// Users module getters
export const getters = make.getters(state);
